// Racket Lab R3 — Service Tickets page (Feb 2026).
//
// Route: /app/racket-lab/tickets
// Role visibility:
//   • racket_technician / admin / staff → see "New ticket" button + can advance state.
//   • player → read-only list of tickets where player_id == me.
//   • All other users → empty list (server filters by ownership).
//
// Sandbox: demo identities create tickets in overlay only. Items
// returned with `_demo_state="created"` are shown with a "Demo overlay"
// badge for honesty.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLang } from "@/i18n";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { RefreshCw, Plus, Wrench, ArrowLeft } from "lucide-react";
import TicketCreateDialog from "@/components/racket_lab/TicketCreateDialog";
import TicketDetailPanel from "@/components/racket_lab/TicketDetailPanel";
import { StatusBadge, friendlyStatus } from "@/components/racket_lab/TicketStatusBits";
import RacketLabNav from "@/components/racket_lab/RacketLabNav";

const TECHNICIAN_ROLES = new Set(["admin", "staff"]);
const TECHNICIAN_PROFILE_KEYS = new Set(["racket_technician"]);

const canCreateTickets = (user) => {
  if (!user) return false;
  if (TECHNICIAN_ROLES.has(user.role)) return true;
  return TECHNICIAN_PROFILE_KEYS.has(user.profile_key);
};

export default function RacketLabTickets() {
  const { t } = useLang();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [filter, setFilter] = useState("all");          // all | active | done
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setDisabled(false);
    try {
      const r = await api.get("/racket-lab/service-tickets/mine?limit=200");
      setItems(r.data?.items ?? []);
    } catch (err) {
      if (err?.response?.status === 404) setDisabled(true);
      else toast.error(t("racketLab.tickets.updateFail"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "active") {
      return items.filter((it) => !["delivered", "cancelled"].includes(it.status));
    }
    if (filter === "done") {
      return items.filter((it) => ["delivered", "cancelled"].includes(it.status));
    }
    return items;
  }, [items, filter]);

  if (disabled) {
    return (
      <div className="max-w-3xl mx-auto" data-testid="racket-lab-tickets-disabled">
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-6">
          <h1 className="text-lg font-semibold text-amber-900">
            {t("racketLab.tickets.disabledTitle")}
          </h1>
          <p className="mt-2 text-sm text-amber-800">
            {t("racketLab.tickets.disabledBody")}
          </p>
        </div>
      </div>
    );
  }

  if (selectedId) {
    return (
      <div className="max-w-4xl mx-auto space-y-4" data-testid="racket-lab-tickets-detail-wrapper">
        <Button
          variant="outline"
          onClick={() => setSelectedId(null)}
          data-testid="racket-lab-tickets-back"
        >
          <ArrowLeft className="h-4 w-4 me-1.5 rtl:rotate-180" />
          {t("racketLab.tickets.back")}
        </Button>
        <TicketDetailPanel
          ticketId={selectedId}
          currentUser={user}
          onUpdated={load}
          onClose={() => setSelectedId(null)}
        />
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="racket-lab-tickets-page">
      <RacketLabNav currentUser={user} t={t} />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-emerald-700">
            <Wrench className="h-5 w-5" />
            <h1 className="text-2xl font-bold text-slate-900 truncate">
              {t("racketLab.tickets.pageTitle")}
            </h1>
          </div>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {t("racketLab.tickets.pageSub")}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="racket-lab-tickets-refresh"
          >
            <RefreshCw className={`h-4 w-4 me-1.5 ${loading ? "animate-spin" : ""}`} />
            {t("racketLab.tickets.refresh")}
          </Button>
          {canCreateTickets(user) && (
            <Button
              onClick={() => setCreateOpen(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="racket-lab-tickets-new"
            >
              <Plus className="h-4 w-4 me-1.5" />
              {t("racketLab.tickets.newTicket")}
            </Button>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div className="flex gap-2" role="tablist">
        {["all", "active", "done"].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            data-active={filter === f ? "true" : "false"}
            data-testid={`racket-lab-tickets-filter-${f}`}
            className={`text-xs px-3 py-1.5 rounded-full border ${
              filter === f
                ? "bg-emerald-600 text-white border-emerald-600"
                : "bg-white text-slate-700 border-slate-200 hover:border-emerald-300"
            }`}
          >
            {t(`racketLab.tickets.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400">
          <RefreshCw className="h-5 w-5 mx-auto animate-spin text-emerald-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div
          className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500"
          data-testid="racket-lab-tickets-empty"
        >
          {t(canCreateTickets(user)
            ? "racketLab.tickets.emptyListTechnician"
            : "racketLab.tickets.emptyListPlayer")}
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
              <tr>
                <th className="text-start px-4 py-2 font-medium">{t("racketLab.tickets.colId")}</th>
                <th className="text-start px-4 py-2 font-medium">{t("racketLab.tickets.colRacket")}</th>
                <th className="text-start px-4 py-2 font-medium">{t("racketLab.tickets.colStatus")}</th>
                <th className="text-start px-4 py-2 font-medium">{t("racketLab.tickets.colUpdated")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => (
                <tr
                  key={it.id}
                  onClick={() => setSelectedId(it.id)}
                  className="border-t border-slate-100 hover:bg-emerald-50/30 cursor-pointer transition"
                  data-testid={`racket-lab-tickets-row-${it.id}`}
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-700">
                    {it.id.slice(0, 8)}…
                    {it._demo_state === "created" && (
                      <span
                        className="ms-2 inline-block rounded px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-800"
                        data-testid={`racket-lab-tickets-row-${it.id}-demo-badge`}
                      >
                        {t("racketLab.tickets.demoBadge")}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-800">{it.racket_model || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge status={it.status} t={t} /></td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                    {(it.updated_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TicketCreateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => { setCreateOpen(false); load(); }}
      />
    </div>
  );
}
